import React, { useState } from 'react';
import { Building2, Home, Sparkles, ShieldCheck, Users, Leaf, Clock, BadgeCheck, DollarSign, ClipboardList, FileCheck, Calendar, CheckCircle, Phone, Mail, MapPin, Star } from 'lucide-react';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Textarea } from './components/ui/textarea';
import { Label } from './components/ui/label';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './components/ui/accordion';
import { Avatar, AvatarFallback } from './components/ui/avatar';
import { toast } from 'sonner';
import { mockServices, mockTestimonials, mockProcess, mockWhyChooseUs, mockContactInfo } from './utils/mockData';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconComponents = {
  Building2, Home, Sparkles, ShieldCheck, Users, Leaf, Clock, BadgeCheck, DollarSign, ClipboardList, FileCheck, Calendar, CheckCircle, MapPin
};

function App() {
  const [quoteForm, setQuoteForm] = useState({
    name: '',
    email: '',
    phone: '',
    service_type: '',
    message: ''
  });

  const [bookingForm, setBookingForm] = useState({
    name: '',
    email: '',
    phone: '',
    service_type: '',
    preferred_date: '',
    address: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/quotes`, quoteForm);
      
      if (response.data.success) {
        toast.success(response.data.message);
        setQuoteForm({ name: '', email: '', phone: '', service_type: '', message: '' });
      }
    } catch (error) {
      console.error('Quote submission error:', error);
      toast.error('Failed to submit quote request. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/bookings`, bookingForm);
      
      if (response.data.success) {
        toast.success(response.data.message);
        setBookingForm({ name: '', email: '', phone: '', service_type: '', preferred_date: '', address: '' });
      }
    } catch (error) {
      console.error('Booking submission error:', error);
      toast.error('Failed to submit booking request. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToBooking = () => {
    document.getElementById('booking-section').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-sm z-50" style={{height: '96px', overflow: 'visible'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-0 h-full">
          <div className="flex justify-between items-center h-full">
            <div className="flex items-center gap-3">
              <img 
                src="/ph-janitorial-logo.png" 
                alt="PH Janitorial Solutions" 
                className="h-40 md:h-64 object-contain"
              />
            </div>
            <nav className="hidden md:flex items-center gap-8">
              <a href="#services" className="text-gray-700 hover:text-[#84D24A] transition-colors font-medium">Services</a>
              <a href="#how-it-works" className="text-gray-700 hover:text-[#84D24A] transition-colors font-medium">How It Works</a>
              <a href="#testimonials" className="text-gray-700 hover:text-[#84D24A] transition-colors font-medium">Testimonials</a>
              <a href="#contact" className="text-gray-700 hover:text-[#84D24A] transition-colors font-medium">Contact</a>
              <Button onClick={scrollToBooking} className="bg-[#84D24A] hover:bg-[#75BB3F] text-white">
                Book Now
              </Button>
            </nav>
            <Button onClick={scrollToBooking} className="md:hidden bg-[#84D24A] hover:bg-[#75BB3F] text-white">
              Book Now
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-28 pb-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-block">
                <span className="text-sm font-semibold text-[#84D24A] bg-[#84D24A]/10 px-4 py-2 rounded-full">
                  Powered by Local Experts. Delivered Nationwide.
                </span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight">
                Professional Cleaning
                <span className="block text-[#84D24A]">You Can Trust</span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                Comprehensive janitorial services across Canada. From commercial spaces to residential homes, 
                our trusted local partners deliver exceptional cleaning with nationwide reliability.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button onClick={scrollToBooking} size="lg" className="bg-[#84D24A] hover:bg-[#75BB3F] text-white text-lg px-8 py-6">
                  Get Free Quote
                </Button>
                <Button size="lg" variant="outline" className="text-lg px-8 py-6 border-2 border-gray-300 hover:border-[#84D24A] hover:text-[#84D24A]">
                  <Phone className="mr-2 h-5 w-5" />
                  {mockContactInfo.phone}
                </Button>
              </div>
              <div className="flex items-center gap-8 pt-4">
                <div>
                  <div className="text-3xl font-bold text-[#84D24A]">500+</div>
                  <div className="text-sm text-gray-600">Happy Clients</div>
                </div>
                <div className="h-12 w-px bg-gray-300"></div>
                <div>
                  <div className="text-3xl font-bold text-[#84D24A]">10+</div>
                  <div className="text-sm text-gray-600">Years Experience</div>
                </div>
                <div className="h-12 w-px bg-gray-300"></div>
                <div>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-[#84D24A] text-[#84D24A]" />
                    ))}
                  </div>
                  <div className="text-sm text-gray-600">5-Star Rated</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1497366754035-f200968a6e72?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85" 
                alt="Professional Cleaning" 
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 bg-[#84D24A] rounded-full flex items-center justify-center">
                    <ShieldCheck className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">Licensed & Insured</div>
                    <div className="text-sm text-gray-600">Your protection guaranteed</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Our Services</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive cleaning solutions tailored to your needs
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {mockServices.map((service) => {
              const IconComponent = iconComponents[service.icon];
              return (
                <Card key={service.id} className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-[#84D24A]">
                  <div className="overflow-hidden rounded-t-lg">
                    <img 
                      src={service.image} 
                      alt={service.title} 
                      className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <CardHeader>
                    <div className="h-14 w-14 bg-[#84D24A]/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#84D24A] transition-colors">
                      <IconComponent className="h-7 w-7 text-[#84D24A] group-hover:text-white transition-colors" />
                    </div>
                    <CardTitle className="text-2xl">{service.title}</CardTitle>
                    <CardDescription className="text-base">{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-gray-700">
                          <CheckCircle className="h-4 w-4 text-[#84D24A] flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Why Choose PH Janitorial Solutions?</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Experience the difference of professional, reliable cleaning services
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mockWhyChooseUs.map((item) => {
              const IconComponent = iconComponents[item.icon];
              return (
                <div key={item.id} className="flex gap-4 p-6 bg-white rounded-xl hover:shadow-lg transition-shadow">
                  <div className="h-12 w-12 bg-[#84D24A]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <IconComponent className="h-6 w-6 text-[#84D24A]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Simple, transparent process from quote to completion
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {mockProcess.map((step) => {
              const IconComponent = iconComponents[step.icon];
              return (
                <div key={step.step} className="relative">
                  <div className="text-center">
                    <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-[#84D24A] text-white text-2xl font-bold mb-4">
                      {step.step}
                    </div>
                    <div className="h-14 w-14 bg-[#84D24A]/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="h-7 w-7 text-[#84D24A]" />
                    </div>
                    <h3 className="font-bold text-xl text-gray-900 mb-3">{step.title}</h3>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                  {step.step < 4 && (
                    <div className="hidden lg:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-[#84D24A]/30"></div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">What Our Clients Say</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Don't just take our word for it - hear from our satisfied customers
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {mockTestimonials.map((testimonial) => (
              <Card key={testimonial.id} className="bg-white hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="flex items-center gap-4 mb-4">
                    <Avatar className="h-12 w-12 bg-[#84D24A]">
                      <AvatarFallback className="text-white font-bold">{testimonial.avatar}</AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="font-bold text-gray-900">{testimonial.name}</div>
                      <div className="text-sm text-gray-600">{testimonial.role}</div>
                      <div className="text-sm text-gray-500">{testimonial.company}</div>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-[#84D24A] text-[#84D24A]" />
                    ))}
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 italic">"{testimonial.text}"</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Booking & Quote Section */}
      <section id="booking-section" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Ready to Get Started?</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Book your service or request a free quote today
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Quote Form */}
            <Card className="border-2">
              <CardHeader>
                <CardTitle className="text-2xl">Request a Free Quote</CardTitle>
                <CardDescription>Fill out the form and we'll get back to you within 24 hours</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleQuoteSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="quote-name">Full Name *</Label>
                    <Input 
                      id="quote-name" 
                      required 
                      value={quoteForm.name}
                      onChange={(e) => setQuoteForm({...quoteForm, name: e.target.value})}
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <Label htmlFor="quote-email">Email *</Label>
                    <Input 
                      id="quote-email" 
                      type="email" 
                      required 
                      value={quoteForm.email}
                      onChange={(e) => setQuoteForm({...quoteForm, email: e.target.value})}
                      placeholder="john@example.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="quote-phone">Phone *</Label>
                    <Input 
                      id="quote-phone" 
                      type="tel" 
                      required 
                      value={quoteForm.phone}
                      onChange={(e) => setQuoteForm({...quoteForm, phone: e.target.value})}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div>
                    <Label htmlFor="quote-service">Service Type *</Label>
                    <select 
                      id="quote-service"
                      required
                      value={quoteForm.service_type}
                      onChange={(e) => setQuoteForm({...quoteForm, service_type: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#84D24A]"
                    >
                      <option value="">Select a service</option>
                      <option value="commercial">Commercial Cleaning</option>
                      <option value="residential">Residential Cleaning</option>
                      <option value="specialized">Specialized Services</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="quote-message">Additional Details</Label>
                    <Textarea 
                      id="quote-message" 
                      rows={4}
                      value={quoteForm.message}
                      onChange={(e) => setQuoteForm({...quoteForm, message: e.target.value})}
                      placeholder="Tell us about your cleaning needs..."
                    />
                  </div>
                  <Button type="submit" disabled={isSubmitting} className="w-full bg-[#84D24A] hover:bg-[#75BB3F] text-white disabled:opacity-50">
                    {isSubmitting ? 'Submitting...' : 'Request Quote'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Booking Form */}
            <Card className="border-2 border-[#84D24A]">
              <CardHeader>
                <CardTitle className="text-2xl">Book Service Now</CardTitle>
                <CardDescription>Schedule your cleaning service at a time that works for you</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleBookingSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="booking-name">Full Name *</Label>
                    <Input 
                      id="booking-name" 
                      required 
                      value={bookingForm.name}
                      onChange={(e) => setBookingForm({...bookingForm, name: e.target.value})}
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <Label htmlFor="booking-email">Email *</Label>
                    <Input 
                      id="booking-email" 
                      type="email" 
                      required 
                      value={bookingForm.email}
                      onChange={(e) => setBookingForm({...bookingForm, email: e.target.value})}
                      placeholder="john@example.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="booking-phone">Phone *</Label>
                    <Input 
                      id="booking-phone" 
                      type="tel" 
                      required 
                      value={bookingForm.phone}
                      onChange={(e) => setBookingForm({...bookingForm, phone: e.target.value})}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div>
                    <Label htmlFor="booking-service">Service Type *</Label>
                    <select 
                      id="booking-service"
                      required
                      value={bookingForm.service_type}
                      onChange={(e) => setBookingForm({...bookingForm, service_type: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#84D24A]"
                    >
                      <option value="">Select a service</option>
                      <option value="commercial">Commercial Cleaning</option>
                      <option value="residential">Residential Cleaning</option>
                      <option value="specialized">Specialized Services</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="booking-date">Preferred Date *</Label>
                    <Input 
                      id="booking-date" 
                      type="date" 
                      required 
                      value={bookingForm.preferred_date}
                      onChange={(e) => setBookingForm({...bookingForm, preferred_date: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="booking-address">Service Address *</Label>
                    <Input 
                      id="booking-address" 
                      required 
                      value={bookingForm.address}
                      onChange={(e) => setBookingForm({...bookingForm, address: e.target.value})}
                      placeholder="123 Main St, City, ST 12345"
                    />
                  </div>
                  <Button type="submit" disabled={isSubmitting} className="w-full bg-[#84D24A] hover:bg-[#75BB3F] text-white disabled:opacity-50">
                    {isSubmitting ? 'Submitting...' : 'Book Service'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600">
              Get answers to common questions about our services
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="bg-white px-6 rounded-lg border-2">
              <AccordionTrigger className="text-lg font-semibold hover:text-[#84D24A]">
                How does your nationwide service network work?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                We partner with trusted local cleaning experts across Canada. This allows us to provide nationwide 
                coverage while ensuring fast response times, personalized service, and consistent quality standards in your community.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="bg-white px-6 rounded-lg border-2">
              <AccordionTrigger className="text-lg font-semibold hover:text-[#84D24A]">
                What types of facilities do you service?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                We service offices & corporate spaces, retail stores, medical & dental clinics, warehouses & industrial 
                facilities, property management & condominiums, as well as residential homes and Airbnb properties.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="bg-white px-6 rounded-lg border-2">
              <AccordionTrigger className="text-lg font-semibold hover:text-[#84D24A]">
                How is pricing determined?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                We offer competitive pricing with customized packages based on your specific needs, facility size, and 
                service frequency. We provide cost-effective solutions with long-term service agreements available. Contact us for a customized quote.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="bg-white px-6 rounded-lg border-2">
              <AccordionTrigger className="text-lg font-semibold hover:text-[#84D24A]">
                Can you handle both one-time and recurring cleaning?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                Absolutely! We offer flexible service options including daily, weekly, monthly recurring cleaning, as well as 
                one-time services for deep cleaning, move-in/out, post-construction, and emergency cleaning situations.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="bg-white px-6 rounded-lg border-2">
              <AccordionTrigger className="text-lg font-semibold hover:text-[#84D24A]">
                What makes PH Janitorial Solutions different?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                Our unique advantage is combining nationwide coverage with local expertise. You get consistent quality standards 
                and reliable service across Canada, backed by responsive local partners who understand your community and can quickly address your needs.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Get In Touch</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Have questions? We're here to help
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="pt-8">
                <div className="h-16 w-16 bg-[#84D24A]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="h-8 w-8 text-[#84D24A]" />
                </div>
                <h3 className="font-bold text-xl mb-2">Phone</h3>
                <a href={`tel:${mockContactInfo.phone}`} className="text-[#84D24A] hover:underline text-lg">
                  {mockContactInfo.phone}
                </a>
                <p className="text-sm text-gray-600 mt-2">{mockContactInfo.hours}</p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="pt-8">
                <div className="h-16 w-16 bg-[#84D24A]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-8 w-8 text-[#84D24A]" />
                </div>
                <h3 className="font-bold text-xl mb-2">Email</h3>
                <a href={`mailto:${mockContactInfo.email}`} className="text-[#84D24A] hover:underline text-lg">
                  {mockContactInfo.email}
                </a>
                <p className="text-sm text-gray-600 mt-2">We'll respond within 24 hours</p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="pt-8">
                <div className="h-16 w-16 bg-[#84D24A]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="h-8 w-8 text-[#84D24A]" />
                </div>
                <h3 className="font-bold text-xl mb-2">Location</h3>
                <p className="text-gray-700">
                  {mockContactInfo.address}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="inline-block bg-white p-3 rounded-lg mb-4">
                <img 
                  src="/ph-janitorial-logo.png" 
                  alt="PH Janitorial Solutions" 
                  className="h-20"
                />
              </div>
              <p className="text-gray-400">
                Professional janitorial services across Canada. Powered by local experts, delivered nationwide with consistent quality and reliability.
              </p>
            </div>
            
            <div>
              <h4 className="font-bold text-lg mb-4">Services</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#services" className="hover:text-[#84D24A] transition-colors">Commercial Cleaning</a></li>
                <li><a href="#services" className="hover:text-[#84D24A] transition-colors">Residential Cleaning</a></li>
                <li><a href="#services" className="hover:text-[#84D24A] transition-colors">Specialized Services</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4">Quick Links</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#how-it-works" className="hover:text-[#84D24A] transition-colors">How It Works</a></li>
                <li><a href="#testimonials" className="hover:text-[#84D24A] transition-colors">Testimonials</a></li>
                <li><a href="#contact" className="hover:text-[#84D24A] transition-colors">Contact Us</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  {mockContactInfo.phone}
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  {mockContactInfo.email}
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; {new Date().getFullYear()} PH Janitorial Solutions. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
